/** What a folder's page shows of uploads into it: it takes dropped files, and asks what an upload needs to know. */
import { useEffect, useState } from "preact/hooks";
import { t } from "../../i18n/index.ts";
import { type DropTarget, useDropTarget } from "../../lib/fileDrop.ts";
import { Button } from "../../ui/Button.tsx";
import { Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { Checkbox } from "../../ui/form.tsx";
import { asked, type Question } from "./uploads.ts";

/** A zip's question: keep it as a file, or unpack it into folders (remembered, unless unticked). */
function ZipQuestion({ q }: { q: Extract<Question, { kind: "zip" }> }) {
  const [remember, setRemember] = useState(true);
  return (
    <>
      <p class="text-sm text-zinc-700">{t("folders.zipWhat", { name: q.name })}</p>
      <Checkbox label={t("folders.remember")} checked={remember} onCheck={setRemember} />
      <DialogActions>
        <Button onClick={() => q.answer({ choice: "keep", remember })}>{t("folders.keepZip")}</Button>
        <Button variant="primary" onClick={() => q.answer({ choice: "unpack", remember })}>
          {t("folders.unpackZip")}
        </Button>
      </DialogActions>
    </>
  );
}

/** A same-title question: keep both, replace that one's file, or skip this one; for all the drop's, if ticked. */
function TitleQuestion({ q }: { q: Extract<Question, { kind: "title" }> }) {
  const [all, setAll] = useState(false);
  return (
    <>
      <p class="text-sm text-zinc-700">{t("folders.titleWhat", { name: q.name, folder: q.folder })}</p>
      <Checkbox label={t("folders.forAll")} checked={all} onCheck={setAll} />
      <DialogActions>
        <Button onClick={() => q.answer({ choice: "skip", all })}>{t("folders.skipIt")}</Button>
        <Button onClick={() => q.answer({ choice: "replace", all })}>{t("folders.replace")}</Button>
        <Button variant="primary" onClick={() => q.answer({ choice: "keep", all })}>
          {t("folders.keepBoth")}
        </Button>
      </DialogActions>
    </>
  );
}

/** The question an upload waits on, if any (closed unanswered: the zip kept, both kept). */
function UploadQuestions() {
  const q = asked.value;
  // Gone (the page left) with a question open: answered as closing it would.
  useEffect(
    () => () => {
      const open = asked.value;
      if (open?.kind === "zip") {
        open.answer({ choice: "keep", remember: false });
      } else if (open) {
        open.answer({ choice: "keep", all: false });
      }
    },
    [],
  );
  if (!q) {
    return null;
  }
  // Closed unanswered (Esc): as its first button. Each question its own dialog, so closing one answers only it.
  const close = () =>
    q.kind === "zip" ? q.answer({ choice: "keep", remember: false }) : q.answer({ choice: "keep", all: false });
  return (
    <Dialog
      key={q.id}
      open={true}
      onClose={close}
      title={q.kind === "zip" ? t("folders.zipTitle") : t("folders.titleTitle", { name: q.name })}
    >
      {q.kind === "zip" ? <ZipQuestion q={q} /> : <TitleQuestion q={q} />}
    </Dialog>
  );
}

/** The items list (a folder's, or its top level) takes files dropped on it (`target`: none, it doesn't), asking there
 *  what an upload needs to. */
export function FolderDrop({ target }: { target: DropTarget | null }) {
  useDropTarget(target);
  return target ? <UploadQuestions /> : null;
}
